import { useMemo, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import PaymentPlanSelector from '@components/PaymentPlanSelector';
import YieldPanel from '@components/YieldPanel';
import VirtualTour from '@components/VirtualTour';
import ProjectShowcase from '@components/ProjectShowcase';
import MapExperience from '@components/MapExperience';
import { useT, type StringKey } from '@lib/i18n';
import { calculatePrice, formatOMR } from '@lib/pricing';
import { useSessionStore } from '@stores/sessionStore';
import type { PaymentPlan, Unit } from '@lib/types';

interface ConfigurePanelProps {
  unit: Unit;
  onContinue: () => void;
  onCancel: () => void;
}

export default function ConfigurePanel({
  unit,
  onContinue,
  onCancel,
}: ConfigurePanelProps) {
  const t = useT();
  const storedPlan = useSessionStore((s) => s.paymentPlan);
  const setStoredPlan = useSessionStore((s) => s.setPaymentPlan);
  const [plan, setPlan] = useState<PaymentPlan>(storedPlan ?? 'monthly');
  // V3 Day 1 — VR modal visibility (local; no need to persist).
  const [showVr, setShowVr] = useState(false);
  // V3 Day 2 — Cinematic project showcase modal.
  const [showShowcase, setShowShowcase] = useState(false);
  // V3 Day 3.1 — Interactive map modal.
  const [showMap, setShowMap] = useState(false);

  const breakdown = useMemo(() => calculatePrice(unit.basePrice), [unit.basePrice]);

  const handlePlanChange = (p: PaymentPlan) => {
    setPlan(p);
    setStoredPlan(p);
  };

  const handleContinue = () => {
    setStoredPlan(plan);
    onContinue();
  };

  return (
    <section className="card p-5 md:p-7 animate-slide-up">
      <header className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl md:text-2xl font-bold text-navy">
          {t('match.configureTitle')}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowShowcase(true)}
            className="!py-1.5 !px-3 text-xs md:text-sm rounded-full font-bold text-navy
                       bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500
                       shadow-md transition-all"
            title={t('showcase.title')}
          >
            {t('showcase.discoverKey2')}
          </button>
          <button
            type="button"
            onClick={() => setShowMap(true)}
            className="!py-1.5 !px-3 text-xs md:text-sm rounded-full font-semibold border border-navy-200 text-navy hover:bg-navy hover:text-gold transition-colors"
            title={t('mapExperience.title')}
          >
            {t('mapExperience.openButton')}
          </button>
          <button
            type="button"
            onClick={() => setShowVr(true)}
            className="btn-gold !py-1.5 !px-3 text-xs md:text-sm"
            title={t('vr.title')}
          >
            {t('vr.button')}
          </button>
          <span className="text-xs text-navy-400 num-en">{unit.unitCode}</span>
        </div>
      </header>

      <div className="space-y-6">
        <PaymentPlanSelector
          plan={plan}
          onChange={handlePlanChange}
          breakdown={breakdown}
        />

        <YieldPanel unit={unit} highlight="mid" />

        <SummaryBox unit={unit} plan={plan} t={t} />
      </div>

      <div className="mt-6 flex flex-col-reverse md:flex-row md:justify-end items-stretch md:items-center gap-3">
        <button type="button" onClick={onCancel} className="btn-ghost">
          <ArrowLeftRight className="w-4 h-4" />
          {t('match.chooseAnother')}
        </button>
        <button type="button" onClick={handleContinue} className="btn-primary">
          {t('match.continueToGuarantees')}
        </button>
      </div>

      {showVr && (
        <VirtualTour unitType={unit.type} onClose={() => setShowVr(false)} />
      )}
      <ProjectShowcase
        open={showShowcase}
        onClose={() => setShowShowcase(false)}
      />
      <MapExperience open={showMap} onClose={() => setShowMap(false)} />
    </section>
  );
}

function SummaryBox({
  unit,
  plan,
  t,
}: {
  unit: Unit;
  plan: PaymentPlan;
  t: (k: StringKey) => string;
}) {
  const breakdown = useMemo(() => calculatePrice(unit.basePrice), [unit.basePrice]);

  const omr = t('common.omr');

  return (
    <div className="bg-navy-900 text-white rounded-xl border-s-4 border-gold-300 p-4 md:p-5">
      <div className="text-xs uppercase tracking-wide text-gold-300/80 mb-2">
        {t('match.summaryFor')}
      </div>
      <div className="text-sm md:text-base font-medium flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-3">
        <span className="num-en font-bold text-gold">{unit.unitCode}</span>
        <Sep />
        <span>{unit.type}</span>
        <Sep />
        <span>
          <span className="num-en">{unit.area}</span> {t('match.areaUnit')}
        </span>
        <Sep />
        <span className="text-white/80">{unit.floor}</span>
      </div>
      <div className="text-sm md:text-base flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {plan === 'cash' && (
          <SumRow label={t('match.priceFinal')} value={breakdown.cashFinal} decimals={0} omr={omr} />
        )}
        {plan === 'monthly' && (
          <>
            <SumRow label={t('match.priceWithVat')} value={breakdown.priceWithVat} decimals={0} omr={omr} />
            <SumRow label={t('match.downPayment')} value={breakdown.downPayment} decimals={0} omr={omr} />
            <SumRow label={t('match.monthlyShort')} value={breakdown.monthlyInst} decimals={2} omr={omr} />
          </>
        )}
        {plan === 'quarterly' && (
          <>
            <SumRow label={t('match.priceWithVat')} value={breakdown.priceWithVat} decimals={0} omr={omr} />
            <SumRow label={t('match.downPayment')} value={breakdown.downPayment} decimals={0} omr={omr} />
            <SumRow label={t('match.quarterlyShort')} value={breakdown.quarterlyInst} decimals={2} omr={omr} />
          </>
        )}
        {plan === 'flex' && (
          <>
            <SumRow label={t('match.priceWithVat')} value={breakdown.priceWithVat} decimals={0} omr={omr} />
            <SumRow label={t('match.downPayment')} value={breakdown.downPayment} decimals={0} omr={omr} />
            <SumRow label={t('match.flexFirstMonths')} value={breakdown.flex.initialMonthly} decimals={0} omr={omr} />
            <SumRow label={t('match.flexAfterMonths')} value={breakdown.flex.midMonthly} decimals={2} omr={omr} />
          </>
        )}
      </div>
    </div>
  );
}

function SumRow({
  label,
  value,
  decimals,
  omr,
}: {
  label: string;
  value: number;
  decimals: number;
  omr: string;
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-white/70 text-xs md:text-sm">{label}:</span>
      <span className="font-bold">
        <span className="num-en">
          {formatOMR(value, { withSymbol: false, decimals })}
        </span>
        <span className="text-xs font-normal text-white/70 ms-1">{omr}</span>
      </span>
    </span>
  );
}

function Sep() {
  return <span className="text-white/30">·</span>;
}
